// Periodo unico da casa dos relatorios.
//
// Antes eram cinco jeitos de escolher periodo (o calendario das telas de
// atendimento, o select de Campanhas, os botoes do Painel da IA e datas
// digitadas em Ligacoes e Conversoes) e so o primeiro acompanhava a troca de
// aba. Agora o seletor mora na casca (ReportsWrapper) e escreve
// `?from=&to=&range=` na URL; toda tela le dali.
//
// - `range` e o preset (today, last7days, last30days, monthToDate) ou custom.
// - `from` e `to` sao unix em segundos, bordas do dia no fuso da CONTA (o
//   mesmo contrato do reportPeriodUnix que as telas de atendimento ja usam).
//
// Tudo aqui e funcao pura: quem chama injeta o fuso e o "agora".

import { utcToZonedTime } from 'date-fns-tz';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import startOfDay from 'date-fns/startOfDay';
import isValid from 'date-fns/isValid';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import {
  DATE_RANGE_TYPES,
  dateRanges,
} from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import {
  DEFAULT_REPORT_TIMEZONE,
  resolveRangeInZone,
  startOfDayInZone,
  endOfDayInZone,
  todayInZone,
} from 'dashboard/helper/reportPeriod';

// Chave de provide/inject: a casca oferece o periodo, a tela embutida usa.
// Tela montada fora da casa (Captain, rota propria de Ligacoes) nao recebe e
// continua com o seletor proprio.
export const REPORTS_SHELL_PERIOD = Symbol('reportsShellPeriod');

export const DEFAULT_SHELL_RANGE = DATE_RANGE_TYPES.LAST_7_DAYS;

// Os botoes do seletor, na ordem do mockup aprovado.
export const SHELL_PERIOD_PRESETS = Object.freeze([
  {
    range: DATE_RANGE_TYPES.TODAY,
    labelKey: 'REPORT.PERIOD_BAR.PRESETS.TODAY',
  },
  {
    range: DATE_RANGE_TYPES.LAST_7_DAYS,
    labelKey: 'REPORT.PERIOD_BAR.PRESETS.LAST_7_DAYS',
  },
  {
    range: DATE_RANGE_TYPES.LAST_30_DAYS,
    labelKey: 'REPORT.PERIOD_BAR.PRESETS.LAST_30_DAYS',
  },
  {
    range: DATE_RANGE_TYPES.MONTH_TO_DATE,
    labelKey: 'REPORT.PERIOD_BAR.PRESETS.MONTH_TO_DATE',
  },
]);

const zoneOf = timeZone => timeZone || DEFAULT_REPORT_TIMEZONE;

// Dia de calendario (Date na meia-noite local) de um unix, no fuso da conta.
export const dayFromUnix = (unix, timeZone) =>
  startOfDay(utcToZonedTime(new Date(unix * 1000), zoneOf(timeZone)));

/** yyyy-MM-dd de um dia de calendario. */
export const isoDay = day => format(day, 'yyyy-MM-dd');

/** Dia de calendario a partir de yyyy-MM-dd, ou null se invalido. */
export const dayFromIso = value => {
  if (!value) return null;
  const day = parseISO(String(value));
  return isValid(day) ? startOfDay(day) : null;
};

const buildPeriod = (range, startDay, endDay, timeZone) => {
  const from = startOfDayInZone(startDay, zoneOf(timeZone));
  const to = endOfDayInZone(endDay, zoneOf(timeZone));
  return {
    range,
    startDay,
    endDay,
    from,
    to,
    startIso: isoDay(startDay),
    endIso: isoDay(endDay),
    // Chave primitiva para watch: o objeto e recriado a cada leitura da URL.
    key: `${range}|${from}|${to}`,
  };
};

/**
 * O periodo que a URL pede. Sem nada na URL, os ultimos 7 dias (o mesmo
 * padrao que as telas de atendimento ja tinham).
 *
 * Preset ancorado em hoje e recalculado no fuso da conta. Datas vindas da URL
 * valem quando o preset nao termina hoje (mes anterior navegado no calendario,
 * link antigo) ou quando o periodo e personalizado: ali quem escolheu o dia foi
 * a pessoa. E a mesma regra do reportPeriodUnix.
 */
const resolvePeriodFromQuery = (query, zone, today, now) => {
  const from = Number(query.from);
  const to = Number(query.to);
  const hasDates = from > 0 && to > 0 && to >= from;
  const requested = query.range ? String(query.range) : '';

  if (requested && requested !== DATE_RANGE_TYPES.CUSTOM_RANGE) {
    const preset = resolveRangeInZone(requested, zone, now);
    if (preset) {
      const endDay = hasDates ? dayFromUnix(to, zone) : today;
      if (!hasDates || isoDay(endDay) === isoDay(today)) {
        return buildPeriod(requested, preset.start, preset.end, zone);
      }
      return buildPeriod(requested, dayFromUnix(from, zone), endDay, zone);
    }
  }

  if (hasDates) {
    return buildPeriod(
      requested || DATE_RANGE_TYPES.CUSTOM_RANGE,
      dayFromUnix(from, zone),
      dayFromUnix(to, zone),
      zone
    );
  }

  const fallback = resolveRangeInZone(DEFAULT_SHELL_RANGE, zone, now);
  return buildPeriod(DEFAULT_SHELL_RANGE, fallback.start, fallback.end, zone);
};

export const resolveShellPeriod = (query = {}, timeZone, now = new Date()) => {
  const zone = zoneOf(timeZone);
  const today = todayInZone(zone, now);
  const period = resolvePeriodFromQuery(query, zone, today, now);
  // Um preset so e ele mesmo quando termina hoje. Link de outro dia mantem as
  // datas antigas com o nome do preset, e quem compara precisa saber disso.
  return { ...period, endsToday: period.endIso === isoDay(today) };
};

/**
 * Query de URL de um periodo. Preset recalcula as bordas; personalizado usa
 * os dias dados (invertidos se vierem trocados).
 */
export const shellPeriodQuery = (
  { range, startDay, endDay },
  timeZone,
  now = new Date()
) => {
  const zone = zoneOf(timeZone);
  let period;
  if (range && range !== DATE_RANGE_TYPES.CUSTOM_RANGE) {
    const preset = resolveRangeInZone(range, zone, now);
    if (preset) period = buildPeriod(range, preset.start, preset.end, zone);
  }
  if (!period) {
    const [first, last] =
      startDay <= endDay ? [startDay, endDay] : [endDay, startDay];
    period = buildPeriod(DATE_RANGE_TYPES.CUSTOM_RANGE, first, last, zone);
  }
  return {
    from: String(period.from),
    to: String(period.to),
    range: period.range,
  };
};

/**
 * Nome do periodo para os resumos dos filtros das telas: o nome longo do
 * calendario ("Ultimos 7 dias") quando existe, senao o do seletor ("Hoje").
 */
export const rangeLabelKey = range => {
  const legacy = dateRanges.find(option => option.value === range);
  if (legacy) return legacy.label;
  const preset = SHELL_PERIOD_PRESETS.find(item => item.range === range);
  return preset ? preset.labelKey : 'REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER';
};

/** Quantos dias de calendario o periodo cobre (hoje sozinho = 1). */
export const periodLengthInDays = ({ startDay, endDay }) =>
  differenceInCalendarDays(endDay, startDay) + 1;

// Os periodos que o Painel da IA aceita (o backend so conhece estes codigos, e
// 'all' e o teto de 90 dias). `range` e o preset da casa que bate exatamente.
export const AI_PANEL_PERIOD_PRESETS = Object.freeze([
  {
    value: 'today',
    days: 1,
    range: DATE_RANGE_TYPES.TODAY,
    labelKey: 'REPORT.PERIOD_BAR.FIXED.TODAY',
  },
  {
    value: '7d',
    days: 7,
    range: DATE_RANGE_TYPES.LAST_7_DAYS,
    labelKey: 'REPORT.PERIOD_BAR.FIXED.LAST_7_DAYS',
  },
  {
    value: '30d',
    days: 30,
    range: DATE_RANGE_TYPES.LAST_30_DAYS,
    labelKey: 'REPORT.PERIOD_BAR.FIXED.LAST_30_DAYS',
  },
  {
    value: 'all',
    days: 90,
    range: null,
    labelKey: 'REPORT.PERIOD_BAR.FIXED.LAST_90_DAYS',
  },
]);

/**
 * Tela que so aceita periodos fixos mostra o menor fixo que cobre o periodo
 * escolhido (03/09 a 19/09 vira "ultimos 30 dias", como no mockup aprovado).
 * `exact` diz se o fixo e o proprio periodo; quando nao e, a casa avisa.
 * O fixo sempre termina hoje, entao so e exato quando o periodo tambem termina
 * (link antigo com range=last7days e datas de outra semana nao e exato).
 */
export const nearestFixedPeriod = (
  period,
  presets = AI_PANEL_PERIOD_PRESETS
) => {
  const same = presets.find(item => item.range && item.range === period.range);
  if (same && period.endsToday) return { ...same, exact: true };
  const length = periodLengthInDays(period);
  const covering =
    presets.find(item => item.days >= length) || presets[presets.length - 1];
  return { ...covering, exact: false };
};
